import * as React from 'react'

import classNames from 'classnames/bind'
import isEmpty from 'lodash/isEmpty'
import type moment from 'moment'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { matchText } from 'modules/core/lib/stringUtils'
import { employeeUser } from 'modules/core/store/collections/users'

import Store from 'modules/calendar/components/TeamView/components/Store'
import TableBody from 'modules/calendar/components/TeamView/components/TableBody'
import TableHeader from 'modules/calendar/components/TeamView/components/TableHeader'
import type Employee from 'modules/employees/store/models/Employee'

import styles from './index.module.scss'

const cx = classNames.bind(styles)

const useSearchFilter = createComputed((search: string, employees: Employee[]) => {
  return employees
    .filter((employee) => isEmpty(search) || matchText(search, employeeUser(employee).fullName()))
    .sort((a, b) => {
      return employeeUser(a).fullName().localeCompare(employeeUser(b).fullName())
    })
})

interface Props {
  date: moment.Moment
  filteredEmployees: Employee[]
  changingData?: boolean
  employeesWorkingDays: Record<string, string>
}

const TeamViewTable = ({
  date,
  changingData = false,
  filteredEmployees,
  employeesWorkingDays,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { access } = useCurrentSession()

  const store = React.useMemo(
    () => new Store(date, filteredEmployees, access),
    [date, filteredEmployees, changingData, access]
  )

  const employeesToShow = useSearchFilter(debouncedSearch, filteredEmployees).get()

  return (
    <div
      className={cx('wrapper', {
        changingData,
      })}
      style={{
        height: '100%',
        width: '100%',
      }}
    >
      <table className={styles.table}>
        <TableHeader store={store} search={search} onSearch={onSearch} />
        <TableBody
          store={store}
          employees={employeesToShow}
          employeesWorkingDays={employeesWorkingDays}
        />
      </table>
    </div>
  )
}

export default observer(TeamViewTable)
